/*
 * convert date to local and UTC
 * to another component
*/
import { Injectable } from '@angular/core';
import { DateTime } from 'luxon';

@Injectable({
    providedIn: 'root'
})
class CyDateTime 
{
    static convertToLocalDate
    (
        utcDate,
        utcTime,
        localFormat
    )
    {
        let utcDT;             
        let localDT;
        let localDate;
        let localTime;
        let midnightDT;     
        let currentDateVal;
        let timezone;
             
        timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
             
        if (utcTime)
        {
            utcDT = DateTime.fromFormat(
                   `${utcDate} ` + 
                   `${utcTime}`,
                   'yyyy-MM-dd hh:mm:ss', {zone: 'UTC'});
 
            localDT = utcDT.setZone(timezone);
            localDate = localDT.toFormat(localFormat);
            localTime = localDT.toFormat('hh:mm:ss');
        }
        else
        {
     
            // calculate the date                        
            currentDateVal = DateTime.now().toFormat('MM/dd/yyyy');
            midnightDT = DateTime.fromFormat(
                            `${currentDateVal} 23:59:00`,
                            `MM/dd/yyyy hh:mm:ss`);
            utcDT = midnightDT.setZone('UTC');
            utcTime = utcDT.toFormat('hh:mm:ss');
         
            utcDT = DateTime.fromFormat(
                        `${utcDate} ${utcTime}`,
                        'yyyy-MM-dd hh:mm:ss', {zone: 'UTC'});
            midnightDT = utcDT.setZone(timezone);
            localDate = midnightDT.toFormat(localFormat);     
        }
             
        return localDate;    
    }
    
    static convertToLocalTime
    (
        utcDate,
        utcTime    
    )
    {
        let utcDT;             
        let localDT;
        let localDate;
        let localTime;
        let midnightDT;     
        let currentDateVal;
        let timezone;
              
        timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
              
        if (utcTime)
        {
            utcDT = DateTime.fromFormat(`${utcDate} ` + 
                                        `${utcTime}`,
                                        'yyyy-MM-dd hh:mm:ss', {zone: 'UTC'});
  
            localDT = utcDT.setZone(timezone);
            localDate = localDT.toFormat('MM/dd/yyyy');
            localTime = localDT.toFormat('HH:mm:ss');
        }
        else
        {
             localTime = '';
        }
             
        return localTime;     
    }    

    static convert24HourToAmPm
    (
        time24h: string | null | undefined
    ): string 
    {
        let time: DateTime;
        let result: string;
    
        time = DateTime.fromFormat(time24h, 'HH:mm:ss');
        if (!time.isValid) 
        {
            time = DateTime.fromFormat(time24h, 'HH:mm');
        }
    
        result = time.toFormat('hh:mm a');

        if (result === 'Invalid DateTime')
        {
            result = '';
        }
        
        return result;
    }

    static convertToLocalDateDisp
    (
        date: string
    )
    {
        let result: string;
        let dateOnly: string;

        result = null; 

        if (!date || date === '0000-00-00')
        {
            result = '';
        }
        else
        {
            // Extract only the date part if datetime includes time
            dateOnly = date.includes(' ') ? date.split(' ')[0] : date;
            result = DateTime.fromFormat(dateOnly, 'yyyy-MM-dd').toFormat('MM/dd/yyyy');
        }

        return result;
    }

    static convertISOToDate
    (
        isoDate: string
    ): string 
    {
        let dt: DateTime;

        dt = DateTime.fromISO(isoDate);
        if (!dt.isValid) 
        {
            return '';
        }
        
        return dt.toFormat('yyyy-MM-dd');
    }

    static convertDateToAPIFormat
    (
        date: Date | string | null | undefined
    ): string | null
    {
        let year: number;
        let month: string; 
        let day: string;
        let formattedDate: string;
        let dateObj: Date;

        if (!date)
        {
            return null;
        }

        if (date instanceof Date)
        {
            dateObj = date;
        }
        else if (typeof date === 'string')
        {
            dateObj = this.parseDateToLocalDate(date);
            if (!dateObj)
            {
                return null;
            }
        }
        else
        {
            return null;
        }

        year = dateObj.getFullYear();
        month = (dateObj.getMonth() + 1).toString().padStart(2, '0');
        day = dateObj.getDate().toString().padStart(2, '0');

        formattedDate = `${year}-${month}-${day}`;

        return formattedDate;
    }

    /**
     * @desc   - Convert date to database format (yyyy-MM-dd)
     *           Handles ISO strings, MM/dd/yyyy format strings, and Date 
     *           objects
     * @access - public
     * @param  - string | Date | null | undefined date - date to convert
     * @return - string | null - date in yyyy-MM-dd format or null if invalid
     */
    static convertDateToDBFormat
    (
        date: string | Date | null | undefined
    ): string | null
    {
        let dt: DateTime;
        let result: string | null;

        if (!date)
        {
            return null;
        }

        if (typeof date === 'string')
        {
            dt = DateTime.fromISO(date);
            if (!dt.isValid)
            {
                dt = DateTime.fromFormat(date, 'MM/dd/yyyy');
            }
        }
        else if (date instanceof Date)
        {
            dt = DateTime.fromJSDate(date);
        }
        else
        {
            console.warn(`Unsupported date type: ${typeof date}`);
            return null;
        }

        if (!dt || !dt.isValid)
        {
            console.warn(`Invalid date: ${date}`);
            return null;
        }

        result = dt.toFormat('yyyy-MM-dd');

        return result;
    }

    /**
     * @desc   - Parses a date string (MM/DD/YYYY or YYYY-MM-DD) or Date object
     *           and converts it to a Date object at local noon to avoid timezone
     *           issues
     * @access - public
     * @param  - string | Date | null | undefined dateInput - date string or 
     *           Date object
     * @return - Date | null - Date object at local noon or null if invalid
     */
    static parseDateToLocalDate
    (
        dateInput: string | Date | null | undefined
    ): Date | null
    {
        let date: Date | null;
        let dateString: string;
        let parts: string[];
        let year: number;
        let month: number;
        let day: number;
        let dateObj: any;

        date = null;

        if (!dateInput)
        {
            return null;
        }

        if (dateInput instanceof Date)
        {
            if (isNaN(dateInput.getTime()))
            {
                return null;
            }
            date = new Date(
                dateInput.getFullYear(),
                dateInput.getMonth(),
                dateInput.getDate(),
                12,
                0,
                0
            );
        }
        else
        {
            dateString = String(dateInput);

            if (dateString.includes('/'))
            {
                parts = dateString.split('/');
                if (parts.length === 3)
                {
                    year = parseInt(parts[2]);
                    month = parseInt(parts[0]) - 1;
                    day = parseInt(parts[1]);
                    date = new Date(year, month, day, 12, 0, 0);
                }
            }
            else if (dateString.includes('-'))
            {
                parts = dateString.split('-');
                if (parts.length === 3)
                {
                    year = parseInt(parts[0]);
                    month = parseInt(parts[1]) - 1;
                    day = parseInt(parts[2]);
                    date = new Date(year, month, day, 12, 0, 0);
                }
            }
            else
            {
                dateObj = new Date(dateString);
                if (!isNaN(dateObj.getTime()))
                {
                    date = new Date(
                        dateObj.getFullYear(),
                        dateObj.getMonth(),
                        dateObj.getDate(),
                        12,
                        0,
                        0
                    );
                }
            }
        }

        if (date && isNaN(date.getTime()))
        {
            return null;
        }

        return date;
    }

    /**
     * @desc - parses YYYY-MM-DD string as local date (avoids UTC shift
     *         when restoring from localStorage so dates do not move by a day)
     * @access - public
     * @param  - dateStr : date string (YYYY-MM-DD)
     * @return - Date at local midnight or null
     */
    static parseLocalDate
    (
        dateStr: string
    ): Date | null
    {
        let parts: number[];
        let y: number;
        let m: number;
        let d: number;
        let date: Date;

        if (!dateStr || typeof dateStr !== 'string')
        {
            date = null;
        }
        parts = dateStr.trim().split('-').map(Number);
        if (parts.length !== 3 || parts.some((n) => isNaN(n)))
        {
            date = null;
        }
        [y, m, d] = parts;
        date = new Date(y, m - 1, d);
        if (isNaN(date.getTime()))
        {
            date = null;
        }

        return date;
    }

    static convertAmPmTo24Hour
    (
        time: string,
        period: string
    ): string
    {
        let timeParts: string[];
        let hours: number;
        let minutes: string;
        let result: string;

        if (!time)
        {
            return '';
        }

        if (time.includes(':'))
        {
            timeParts = time.split(':');
            hours = parseInt(timeParts[0]);
            minutes = timeParts[1] || '00';
        }
        else
        {
            hours = parseInt(time);
            minutes = '00';
        }

        if (period === 'PM' && hours !== 12)
        {
            hours += 12;
        }
        else if (period === 'AM' && hours === 12)
        {
            hours = 0;
        }

        result = `${hours.toString().padStart(2, '0')}:${minutes}:00`;

        return result;
    }


    /**
     * @desc   - Convert time string to database format (HH:mm:ss)
     * @access - public
     * @param  - string | null | undefined timeInput : time value
     * @return - string | null : normalized HH:mm:ss or null if invalid
     */
    static convertTimeToDBFormat
    (
        timeInput: string | null | undefined
    ): string | null
    {
        let input: string;
        let match24: RegExpMatchArray | null;
        let match12: RegExpMatchArray | null;
        let hour: number;
        let minute: number;
        let second: number;
        let period: string;
        let result: string | null;

        input = '';
        match24 = null;
        match12 = null;
        hour = 0;
        minute = 0;
        second = 0;
        period = '';
        result = null;

        if (!timeInput)
        {
            return result;
        }

        input = String(timeInput).trim();

        match24 = input.match(/^([01]?\d|2[0-3]):([0-5]\d)(?::([0-5]\d))?$/);
        if (match24)
        {
            hour = parseInt(match24[1], 10);
            minute = parseInt(match24[2], 10);
            second = match24[3] ? parseInt(match24[3], 10) : 0;
            result = `${String(hour).padStart(2, '0')}:`
                + `${String(minute).padStart(2, '0')}:`
                + `${String(second).padStart(2, '0')}`;

            return result;
        }

        match12 = input.match(/^(0?[1-9]|1[0-2]):([0-5]\d)\s*([AaPp][Mm])$/);
        if (match12)
        {
            hour = parseInt(match12[1], 10);
            minute = parseInt(match12[2], 10);
            period = match12[3].toUpperCase();

            if (period === 'AM' && hour === 12)
            {
                hour = 0;
            }
            else if (period === 'PM' && hour < 12)
            {
                hour += 12;
            }

            second = 0;
            result = `${String(hour).padStart(2, '0')}:`
                + `${String(minute).padStart(2, '0')}:`
                + `${String(second).padStart(2, '0')}`;
        }

        return result;
    }

    /**
     * @desc   - Formats date from yyyy-mm-dd to mm/dd/yyyy
     * @access - public
     * @param  - string | Date dateInput - date in yyyy-mm-dd format or 
     *           Date object
     * @return - string - date in mm/dd/yyyy format
     */
    static formatDate
    (
        dateInput: string | Date | null | undefined
    ): string
    {
        let date: any;
        let day: string;
        let month: string;
        let year: string;
        let formattedDate: string;
        let parts: string[];
        let dateString: string;

        formattedDate = '';

        if (!dateInput)
        {
            formattedDate = dateInput as string || '';
        }
        else if (dateInput instanceof Date)
        {
            if (isNaN(dateInput.getTime()))
            {
                formattedDate = '';
            }
            else
            {
                month = (dateInput.getMonth() + 1).toString().padStart(2, '0');
                day = dateInput.getDate().toString().padStart(2, '0');
                year = dateInput.getFullYear().toString();
                formattedDate = `${month}/${day}/${year}`;
            }
        }
        else
        {
            dateString = String(dateInput);

            if (dateString.includes('/'))
            {
                formattedDate = dateString;
            }
            else if (dateString.includes('-'))
            {
                parts = dateString.split('-');
                if (parts.length === 3)
                {
                    year = parts[0];
                    month = parts[1];
                    day = parts[2];
                    date = new Date(
                        parseInt(year), 
                        parseInt(month) - 1, 
                        parseInt(day)
                    );
                    
                    if (isNaN(date.getTime()))
                    {
                        formattedDate = dateString; 
                    }
                    else
                    {
                        month = (date.getMonth() + 1).toString()
                            .padStart(2, '0');
                        day = date.getDate().toString().padStart(2, '0');
                        year = date.getFullYear().toString();
                        formattedDate = `${month}/${day}/${year}`;
                    }
                }
                else
                {
                    formattedDate = dateString;
                }
            }
            else
            {
                date = new Date(dateString);
                if (isNaN(date.getTime()))
                {
                    formattedDate = dateString; 
                }
                else
                {
                    month = (date.getMonth() + 1).toString().padStart(2, '0');
                    day = date.getDate().toString().padStart(2, '0');
                    year = date.getFullYear().toString();
                    formattedDate = `${month}/${day}/${year}`;
                }
            }
        }

        return formattedDate;
    }

    /**
     * @desc   - Formats time from HH:mm:ss to HH:mm AM/PM
     * @access - public
     * @param  - string timeString - time in HH:mm:ss format
     * @return - string - time in HH:mm AM/PM format
     */
    static formatTime
    (
        timeString: string
    ): string
    {
        let time: any;
        let hours: number;
        let minutes: number;
        let ampm: string;
        let formattedTime: string;

        formattedTime = '';

        if (!timeString) return timeString;

        if (timeString)
        {
            time = new Date(`2000-01-01T${timeString}`);
            if (isNaN(time.getTime()))
            {
                formattedTime = timeString;
            }
            else
            {
                hours = time.getHours();
                minutes = time.getMinutes();
                ampm = hours >= 12 ? 'PM' : 'AM';
                hours = hours % 12;
                hours = hours ? hours : 12;
                formattedTime = 
                    `${hours}:${minutes.toString().padStart(2, '0')} ${ampm}`;
            }
        }

        return formattedTime;
    }

    /**
     * @desc   - Format docket dates for display (from-to date range)
     * @access - public
     * @param  - object docket - docket object with fromDate and toDate
     * @return - string - formatted date range or empty string
     */
    static getDocketDateLabel
    (
        docket: { fromDate?: string; toDate?: string }
    ): string
    {
        let fromStr: string;
        let toStr: string;
        let toDate: string;
        let result: string;

        if (!docket?.fromDate)
        {
            return '';
        }
        fromStr = CyDateTime.formatDate(docket.fromDate) || 
                 docket.fromDate;
        toDate = docket.toDate || docket.fromDate;
        toStr = CyDateTime.formatDate(toDate) || toDate;
        result = `${fromStr} – ${toStr}`;
        
        return result;
    }
}

export { CyDateTime }